import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, ArrowLeft, AlertCircle, Loader } from 'lucide-react';
import { API_BASE } from '../../services/api';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #008752 0%, #006B41 50%, #004D30 100%);
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing['2xl']};
  width: 100%;
  max-width: 450px;
  text-align: center;
`;

const LogoImg = styled.img`
  height: 70px;
  width: auto;
  margin: 0 auto ${({ theme }) => theme.spacing.lg};
  display: block;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
`;

const AzureButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg};
  background: #0078d4;
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #106ebe;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: pointer;
  margin-left: auto;
  margin-right: auto;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.colors.error}15;
  border: 1px solid ${({ theme }) => theme.colors.error}40;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

interface AzureLoginProps {
  onBack: () => void;
}

const AzureLogin: React.FC<AzureLoginProps> = ({ onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [azureConfig, setAzureConfig] = useState<any>(null);
  const { loginWithAzure } = useAuth();

  useEffect(() => {
    // Obtener configuración de Azure AD
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/azure-config`);
        const config = await response.json();
        setAzureConfig(config);
      } catch (err) {
        console.error('Error obteniendo config Azure:', err);
      }
    };
    fetchConfig();
  }, []);

  const handleAzureLogin = async () => {
    if (!azureConfig) {
      setError('Configuración de Azure no disponible');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Construir URL de autorización de Azure AD
      const authUrl = new URL(`https://login.microsoftonline.com/${azureConfig.tenantId}/oauth2/v2.0/authorize`);
      authUrl.searchParams.set('client_id', azureConfig.clientId);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('redirect_uri', azureConfig.redirectUri);
      authUrl.searchParams.set('scope', 'openid profile email User.Read');
      authUrl.searchParams.set('response_mode', 'query');

      // Redirigir a Azure para autenticación
      window.location.href = authUrl.toString();
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión con Azure');
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <LogoImg src="/logo-alianza-team.png" alt="Alianza Team" />
        <Title>Acceso Interno</Title>
        <Subtitle>Inicie sesión con su cuenta de Alianza Team</Subtitle>

        {error && (
          <ErrorMessage>
            <AlertCircle size={16} />
            {error}
          </ErrorMessage>
        )}

        {isLoading ? (
          <LoadingSpinner>
            <Loader size={32} />
            <span>Conectando con Azure AD...</span>
          </LoadingSpinner>
        ) : (
          <AzureButton onClick={handleAzureLogin} disabled={!azureConfig}>
            <Building2 size={20} />
            Iniciar sesión con Microsoft
          </AzureButton>
        )}

        <BackButton onClick={onBack}>
          <ArrowLeft size={16} />
          Volver a selección
        </BackButton>
      </Card>
    </Container>
  );
};

export default AzureLogin;

